import * as appActions from "contexts/appsettings/interface";
import * as hierarchyActions from "contexts/hierarchy/interface";
import * as tlfActions from "contexts/tlf/interface";
import { editablePatch } from "./editablePatch";
import { IUnifiedAction, IUnifiedState } from "./interface";
import { isTlfEditablePath } from "./isTlfEditablePath";
import { toEditableData } from "./toEditableData";

/** Domain operations representing local edits rather than synchronisation or UI changes. */
const editableOperations: Set<IUnifiedAction["operation"]> = new Set([
  hierarchyActions.ADD_DOMAIN_REPLACEMENT,
  hierarchyActions.ADD_SINGLE_REPLACEMENT,
  hierarchyActions.REMOVE_DOMAIN_REPLACEMENT,
  hierarchyActions.REMOVE_SINGLE_REPLACEMENT,
  hierarchyActions.UPDATE_DOMAIN_REPLACEMENT,
  hierarchyActions.UPDATE_SINGLE_REPLACEMENT,
  hierarchyActions.UPDATE_HIERARCHY,
  tlfActions.ACTIVATE_VARIANT_PATCH,
  tlfActions.APPLY_VARIANT_TEMPLATE,
  tlfActions.CANCEL_VARIANT_PATCH_CREATION,
  tlfActions.CREATE_VARIANT_PATCH,
  tlfActions.REMOVE_VARIANT_PATCH,
  tlfActions.REMOVE_VARIANT_TEMPLATE,
  tlfActions.RENAME_VARIANT_PATCH,
  tlfActions.RENAME_VARIANT_TEMPLATE,
  tlfActions.REVERT_TO_VARIANT_MASTER,
  tlfActions.SET_VARIANT_CREATION_VIEW,
  tlfActions.SET_VARIANT_PATCH_SELECTION,
  tlfActions.TOGGLE_VARIANT_PATCH,
  tlfActions.UPDATE_TLF,
  tlfActions.UPSERT_VARIANT_TEMPLATE,
]);

/**
 * Finishes an ordinary action with navigation resets or reversible local history.
 *
 * @param previous - State before the domain action.
 * @param next - State after the domain action.
 * @param action - Action whose navigation and history flags determine recording.
 * @returns State with updated chronological history.
 */
export const finishHistory = (
  previous: IUnifiedState,
  next: IUnifiedState,
  action: IUnifiedAction,
): IUnifiedState => {
  /** If a undo stack should be reset due to certain actions. */
  const reset =
    (action.operation === appActions.SAVE_COMPLETED &&
      (previous.hierarchyState.hierarchy?.id !== next.hierarchyState.hierarchy?.id ||
        previous.tlfState.tlfData?.id !== next.tlfState.tlfData?.id)) ||
    action.operation === tlfActions.LOAD_TLF ||
    action.operation === tlfActions.CLEAR_TLF ||
    action.operation === hierarchyActions.LOAD_HIERARCHY ||
    action.operation === hierarchyActions.CLEAR_HIERARCHY ||
    (action.operation === hierarchyActions.SET_HIERARCHY_ID &&
      previous.hierarchyState.id !== next.hierarchyState.id) ||
    (action.operation === hierarchyActions.SAVE_HIERARCHY_COMPLETED &&
      previous.hierarchyState.hierarchy?.id !== next.hierarchyState.hierarchy?.id) ||
    (action.operation === tlfActions.UPDATE_TLF &&
      action.newSavePoint &&
      previous.tlfState.tlfData?.id !== next.tlfState.tlfData?.id) ||
    (action.operation === tlfActions.UPDATE_TLF && action.reset);
  if (reset) return { ...next, undoHistory: [], undoIndex: 0 };

  const isExternal =
    (action.operation === tlfActions.UPDATE_TLF && (action.newSavePoint || action.doNotAddUndo)) ||
    (action.operation === hierarchyActions.UPDATE_HIERARCHY &&
      (!!action.savedHierarchy || action.isSynchronisation === true)) ||
    ("calledFrom" in action && action.calledFrom?.startsWith("SignalR") === true);

  if (
    (action.operation === tlfActions.UPDATE_TLF && action.newSavePoint) ||
    action.operation === tlfActions.TRACK_ACTIVE_VARIANT_PATCH
  ) {
    const before = toEditableData(previous);
    const after = toEditableData(next);
    const normalisation = editablePatch(before, after);
    const index = previous.undoHistory
      .slice(0, previous.undoIndex)
      .findLastIndex((entry) => entry.forward.some((patch) => isTlfEditablePath(patch.path)));
    if (normalisation.length > 0 && index >= 0) {
      // Fold derived catalogues or server normalisation into their originating TLF edit.
      const history = previous.undoHistory.slice(0, previous.undoIndex);
      const entry = history[index];
      history[index] = {
        forward: [...entry.forward, ...normalisation],
        reverse: [...editablePatch(after, before), ...entry.reverse],
      };
      return { ...next, undoHistory: history };
    }
  }

  if (!isExternal && editableOperations.has(action.operation)) {
    const before = toEditableData(previous);
    const after = toEditableData(next);
    const forward = editablePatch(before, after);
    if (forward.length > 0) {
      const history = previous.undoHistory.slice(0, previous.undoIndex);
      history.push({ forward, reverse: editablePatch(after, before) });
      return { ...next, undoHistory: history, undoIndex: history.length };
    }
  }

  if (
    (isExternal || !editableOperations.has(action.operation)) &&
    (previous.tlfState.tlfData !== next.tlfState.tlfData ||
      previous.tlfState.masterTlfData !== next.tlfState.masterTlfData ||
      previous.tlfState.appliedVariantPatches !== next.tlfState.appliedVariantPatches ||
      previous.tlfState.activeVariantPatchName !== next.tlfState.activeVariantPatchName ||
      previous.tlfState.variantCreationView !== next.tlfState.variantCreationView ||
      previous.hierarchyState.hierarchy !== next.hierarchyState.hierarchy ||
      previous.hierarchyState.hierarchyNodes !== next.hierarchyState.hierarchyNodes)
  ) {
    // Remote writes win; overlapping changes invalidate the whole dependent patch sequence.
    const remote = editablePatch(toEditableData(previous), toEditableData(next));
    /**
     * Identifies local patch paths overwritten by remote synchronisation.
     *
     * @param path - Local editable-data JSON pointer.
     * @returns Whether a remote write replaces this path or one of its ancestors or descendants.
     */
    const overlaps = (path: string): boolean =>
      remote.some((patch) => {
        // Array insertion/removal shifts indices, so conflicts cover the entire collection.
        const localPath = path.replace(/\/\d+(\/.*)?$/, "");
        const remotePath = patch.path.replace(/\/\d+(\/.*)?$/, "");
        return (
          localPath === remotePath ||
          localPath.startsWith(`${remotePath}/`) ||
          remotePath.startsWith(`${localPath}/`)
        );
      });
    const conflict = previous.undoHistory.some((entry) =>
      [...entry.forward, ...entry.reverse].some((patch) => overlaps(patch.path)),
    );
    if (conflict) return { ...next, undoHistory: [], undoIndex: 0 };
  }
  return next;
};
